import {
  faAngleDown,
  faAngleRight,
  faAngleUp,
  faArrowLeft,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
// eslint-disable-next-line no-restricted-imports
import MuiDrawer from '@mui/material/Drawer';
import React, { useState, useTransition } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DaxkoLogo from 'src/assets/img/daxko-logo.svg';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { List, ListItem, ListItemProps } from 'src/components/ListItem';
import { Scroller } from 'src/components/Scroller';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Theme } from 'src/components/muiTheme/types';
import {
  ClickAwayListener,
  SxProps,
  useMediaQuery,
} from 'src/components/muiTheme/utils';
import { Collapse } from 'src/components/transitions/Collapse';
import { useOptionalSession } from 'src/contexts/SessionContext';

import { ActionsMenuDreamcoat } from './ActionsMenuDreamcoat';
import { MessagesIndicator } from './MessagesIndicator';
import {
  DecoratedNavItem,
  getNavItemChildren,
  hasNavItemChildren,
  isSectionedNavChildren,
} from './navUtils';
import { getSolidIcon } from './routeConfig';
import { useAdminNav } from './useAdminNav';
import { useMainNav } from './useMainNav';

const DreamcoatNavColors = {
  applicationMenuBg: '#014271',
  applicationMenuItemHighlight: '#025B9C',
  primaryHighlight: '#038BED',
  submenuBg: '#232236',
  submenuItemTitle: '#C0C0C6',
};

export interface MainNavProps {
  open: boolean;
  toggleOpen: () => void;
  customNavItems?: DecoratedNavItem[];
  navWidth?: string;
  isAdmin?: boolean;
}

export const MainNavDreamcoat: React.FC<MainNavProps> = ({
  open,
  toggleOpen,
  customNavItems,
  navWidth = '84px',
  isAdmin = false,
}) => {
  const [activeSubmenu, setActiveSubmenu] = useState<DecoratedNavItem | null>(
    null
  );
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<
    Record<string, boolean>
  >({});
  const { session } = useOptionalSession();

  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const mainNavItems = useMainNav();
  const navItemsToRender = customNavItems ? customNavItems : mainNavItems;

  const toggleSection = (sectionId: string) => {
    setCollapsedSectionIds((current) => ({
      ...current,
      [sectionId]: !current[sectionId],
    }));
  };

  return (
    <ClickAwayListener
      onClickAway={() => activeSubmenu && setActiveSubmenu(null)}
    >
      <MuiDrawer
        open={open}
        anchor="left"
        disableEnforceFocus
        PaperProps={{
          elevation: 0,
          sx: { bgcolor: 'transparent', p: 0 },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: smDown ? '100%' : navWidth,
            height: '100%',
            p: 0,
            overflow: 'visible',
          },
          '&& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, .15)',
          },
        }}
        onClose={toggleOpen}
        variant={smDown ? 'temporary' : 'permanent'}
      >
        <Stack
          direction="column"
          sx={{
            width: smDown ? '100%' : navWidth,
            height: '100%',
            maxHeight: '100%',
            minHeight: 0,
            zIndex: 2,
            position: 'relative',
            bgcolor: DreamcoatNavColors.applicationMenuBg,
            overflowY: 'auto',
          }}
        >
          {smDown ? (
            <Stack
              justifyContent={'space-between'}
              sx={{
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 2, py: 1.5 }}
                >
                  <Stack direction="row" alignItems="center">
                    <img
                      src={DaxkoLogo}
                      alt="Daxko Logo"
                      width="24px"
                      height="24px"
                    />
                    <Typography
                      color="white"
                      variant="h5"
                      fontWeight={400}
                      sx={{ ml: 1 }}
                      // eslint-disable-next-line i18next/no-literal-string
                    >
                      Exercise
                    </Typography>
                  </Stack>

                  <IconButton className="dark" onClick={toggleOpen}>
                    <Icon size="small" icon={faTimes} />
                  </IconButton>
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 2.5, pb: 2 }}
                >
                  <Typography color="white" variant="body1">
                    {session?.brand}
                  </Typography>

                  <div className="dark">
                    <ActionsMenuDreamcoat size="small" hideUserMenu />
                  </div>
                </Stack>

                <Scroller
                  direction="column"
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    height: 'auto',
                    maxHeight: 'none',
                    width: '100%',
                  }}
                >
                  {isAdmin ? (
                    <AdminNavItems
                      activeSubmenu={activeSubmenu}
                      setActiveSubmenu={setActiveSubmenu}
                    />
                  ) : (
                    <MainNavItems
                      activeSubmenu={activeSubmenu}
                      setActiveSubmenu={setActiveSubmenu}
                      navItems={navItemsToRender}
                      placement="top"
                    />
                  )}
                </Scroller>
              </Box>

              {!isAdmin && (
                <BottomNavItems
                  activeSubmenu={activeSubmenu}
                  setActiveSubmenu={setActiveSubmenu}
                  navItems={navItemsToRender}
                  sx={{ flexShrink: 0 }}
                />
              )}
            </Stack>
          ) : (
            <>
              <Stack
                direction="column"
                alignItems="center"
                sx={{
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Box sx={{ mt: 2, mb: 2.25 }}>
                  <img
                    src={DaxkoLogo}
                    alt="Daxko Logo"
                    width="40px"
                    height="40px"
                  />
                </Box>

                <Scroller
                  direction="column"
                  hideScrollbar
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    height: 'auto',
                    maxHeight: 'none',
                    overflowY: 'auto',
                    width: '100%',
                  }}
                >
                  {isAdmin ? (
                    <AdminNavItems
                      activeSubmenu={activeSubmenu}
                      setActiveSubmenu={setActiveSubmenu}
                    />
                  ) : (
                    <MainNavItems
                      activeSubmenu={activeSubmenu}
                      setActiveSubmenu={setActiveSubmenu}
                      navItems={navItemsToRender}
                      placement="top"
                    />
                  )}
                </Scroller>
              </Stack>

              {!isAdmin && (
                <BottomNavItems
                  activeSubmenu={activeSubmenu}
                  setActiveSubmenu={setActiveSubmenu}
                  navItems={navItemsToRender}
                />
              )}
            </>
          )}
        </Stack>

        <Stack
          sx={{
            bgcolor: DreamcoatNavColors.submenuBg,
            p: 0,
            transform: activeSubmenu ? 'translateX(0)' : 'translateX(-100%)',
            opacity: activeSubmenu ? 1 : 0,
            pointerEvents: activeSubmenu ? 'auto' : 'none',
            transition: 'all 0.2s ease-in-out',
            zIndex: smDown ? 4 : 1,
            position: 'absolute',
            top: '0px',
            left: smDown ? '0px' : navWidth,
            height: '100%',
            width: smDown ? '100%' : '280px',
          }}
        >
          {smDown ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 1 }}
            >
              <Stack direction="row" alignItems="center" sx={{ ml: -1 }}>
                <IconButton
                  className="dark"
                  onClick={() => setActiveSubmenu(null)}
                >
                  <Icon size="small" icon={faArrowLeft} />
                </IconButton>
                <Typography color="white" variant="h5">
                  {activeSubmenu?.name}
                </Typography>
              </Stack>
              <IconButton
                className="dark"
                onClick={() => {
                  toggleOpen();
                  setActiveSubmenu(null);
                }}
              >
                <Icon size="small" icon={faTimes} />
              </IconButton>
            </Stack>
          ) : (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography
                color="white"
                fontWeight={600}
                sx={{ fontSize: '24px', lineHeight: '32px' }}
              >
                {session?.brand}
              </Typography>
              <Typography
                color="white"
                variant="h6"
                fontWeight={400}
                sx={{ fontSize: '16px', lineHeight: '24px' }}
              >
                {activeSubmenu?.name}
              </Typography>
            </Box>
          )}
          <Scroller
            direction="column"
            sx={{ px: 1, pb: 2, pt: 1.5, maxHeight: '100%' }}
          >
            <SubmenuItems
              activeSubmenu={activeSubmenu}
              collapsedSectionIds={collapsedSectionIds}
              setActiveSubmenu={setActiveSubmenu}
              toggleSection={toggleSection}
            />
          </Scroller>
        </Stack>
      </MuiDrawer>
    </ClickAwayListener>
  );
};

export interface TopLevelNavItemProps {
  title: string;
  route?: string;
  reactRoute?: string;
  icon?: ListItemProps['icon'];
  childrenItems?: DecoratedNavItem[];
  onClick?: () => void;
  subnavOpen?: boolean;
  target?: string;
  rel?: string;
  level?: number;
  subItem?: boolean;
}

const normalizePath = (path?: string) => path?.replace(/\/$/, '');

const isExternalNavItem = ({
  reactRoute,
  route,
  target,
}: Pick<TopLevelNavItemProps, 'reactRoute' | 'route' | 'target'>) => {
  return target === '_blank' && !!(reactRoute || route);
};

const getNavItemPath = ({
  reactRoute,
  route,
  target,
}: Pick<TopLevelNavItemProps, 'reactRoute' | 'route' | 'target'>) => {
  const isOutsideLink = isExternalNavItem({ reactRoute, route, target });
  let path =
    reactRoute ||
    (route ? `${isOutsideLink ? '' : '/embed'}${route}` : undefined);

  // hack because of bad route data
  if (path?.startsWith('/embed/dashboard/fbm/schedule')) {
    path = '/embed/fbm/schedule/';
  }

  return path;
};

const isNavItemSelected = (
  pathname: string,
  item: Pick<TopLevelNavItemProps, 'reactRoute' | 'route' | 'target'>
) => {
  const path = getNavItemPath(item);

  return !!path && normalizePath(pathname) === normalizePath(path);
};

const hasSelectedChild = (
  pathname: string,
  childrenItems?: DecoratedNavItem[]
): boolean => {
  return !!childrenItems?.some((child) => {
    return (
      isNavItemSelected(pathname, child) ||
      hasSelectedChild(pathname, getNavItemChildren(child))
    );
  });
};

export const TopLevelNavItem: React.FC<TopLevelNavItemProps> = ({
  title,
  icon,
  route,
  reactRoute,
  childrenItems,
  onClick,
  subnavOpen,
  target,
  rel,
  subItem,
}) => {
  const opensSubmenu = !route && !reactRoute;
  const location = useLocation();
  const navigate = useNavigate();
  // Only used when NavItem has children
  const [, startTransition] = useTransition();

  const { pathname } = location;
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const navItemStyles = () => {
    return {
      p: 0,
      py: 1.1,
      m: 0,
      display: 'flex',
      gap: 0.5,
      px: { xs: 2, sm: 0 },
      flexDirection: { xs: 'row', sm: 'column' },
      color: 'primary.contrastText',
      position: 'relative',
      width: '100%',
      '&&&& .MuiListItemIcon-root svg': {
        color: 'primary.contrastText',
        fontSize: '16px',
        mb: { xs: 0, sm: 0.5 },
      },
      '&&&& .MuiListItemText-root .MuiTypography-root': {
        color: 'primary.contrastText',
        fontSize: { xs: '16px', sm: '10px' },
        lineHeight: { xs: '1.2em', sm: '12px' },
        fontWeight: { xs: 400, sm: 600 },
        textAlign: { xs: 'left', sm: 'center' },
        px: 1,
      },
      ...(subnavOpen && {
        bgcolor: DreamcoatNavColors.applicationMenuItemHighlight + '80',
      }),
      '&:hover': {
        bgcolor: DreamcoatNavColors.applicationMenuItemHighlight + '80',
      },
      '&&.Mui-selected': {
        bgcolor: DreamcoatNavColors.applicationMenuItemHighlight,
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: DreamcoatNavColors.primaryHighlight,
        ...(opensSubmenu && subnavOpen
          ? {
              '*:after': {
                content: '""',
                position: 'absolute',
                top: 'calc(50% - 15px)',
                right: '-1px',
                width: '0px',
                height: '0px',
                borderTop: '15px solid transparent',
                borderBottom: '15px solid transparent',
                borderRight: `10px solid ${DreamcoatNavColors.submenuBg}`,
              },
            }
          : {}),
      },
    };
  };

  const subItemStyles = () => {
    return {
      p: 0,
      flexGrow: 0,
      minHeight: '36px',
      borderBottom: '1px solid',
      borderColor: 'rgba(255, 255, 255, 0.13)',
      '& .MuiListItemText-root .MuiTypography-root': {
        color: 'primary.contrastText',
        fontSize: '14px',
        lineHeight: '16px',
        fontWeight: 300,
        p: '10px',
        display: 'flex',
        alignItems: 'center',
      },
      '&.Mui-selected': {
        bgcolor: 'transparent',
      },
    };
  };

  const isMessages = title === 'Messages';

  const isOutsideLink = isExternalNavItem({ reactRoute, route, target });
  const path = getNavItemPath({ reactRoute, route, target });
  const isRootPath =
    pathname == '/embed/dashboard' &&
    path?.startsWith('/embed/dashboard/trainer-dashboard');

  const selected =
    isNavItemSelected(pathname, { reactRoute, route, target }) ||
    hasSelectedChild(pathname, childrenItems) ||
    isRootPath;
  const displayIcon = selected || subnavOpen ? getSolidIcon(icon) : icon;

  const handleClick = (event?: React.MouseEvent) => {
    if (onClick) {
      onClick();
    }
    if (isOutsideLink) {
      return;
    }

    if (path) {
      event?.preventDefault();
      startTransition(() => {
        navigate(path);
      });
    }
  };

  return (
    <ListItem
      component={isOutsideLink ? 'a' : path ? Link : 'button'}
      {...(isOutsideLink ? { href: path } : path ? { to: path } : {})}
      onClick={handleClick}
      target={target}
      sx={subItem ? subItemStyles() : navItemStyles()}
      primary={title}
      icon={displayIcon}
      selected={selected}
      rightAdornment={
        opensSubmenu || isMessages ? (
          <>
            {isMessages && <MessagesIndicator />}
            {opensSubmenu && smDown && (
              <Icon color="inherit" size="xsmall" icon={faAngleRight} />
            )}
          </>
        ) : undefined
      }
      rel={rel}
      clickable
      // To force embed page to update when navigating
      // between pages that are both on the embed route
      state={
        target === '_blank'
          ? undefined
          : {
              renderTime: Date.now(),
            }
      }
    />
  );
};

const SubmenuItems = ({
  activeSubmenu,
  collapsedSectionIds,
  setActiveSubmenu,
  toggleSection,
}: {
  activeSubmenu: DecoratedNavItem | null;
  collapsedSectionIds: Record<string, boolean>;
  setActiveSubmenu: (id: DecoratedNavItem | null) => void;
  toggleSection: (sectionId: string) => void;
}) => {
  const renderSubItem = (subItem: DecoratedNavItem, index: number) => {
    if (!subItem.enabled) {
      return null;
    }

    return (
      <TopLevelNavItem
        key={subItem.id + index}
        title={subItem.name}
        route={subItem.route}
        reactRoute={subItem.reactRoute}
        onClick={() => {
          setActiveSubmenu(null);
        }}
        target={subItem.target}
        rel={subItem.rel}
        subItem
      />
    );
  };

  if (!activeSubmenu?.children) {
    return null;
  }

  if (!isSectionedNavChildren(activeSubmenu.children)) {
    return <>{getNavItemChildren(activeSubmenu).map(renderSubItem)}</>;
  }

  return (
    <>
      {(activeSubmenu.children.items || []).map(renderSubItem)}
      {(activeSubmenu.children.sections || []).map((section) => {
        const sectionKey = `${activeSubmenu.id}-${section.id}`;
        const collapsed =
          section.collapsible && collapsedSectionIds[sectionKey];

        return (
          <Box key={section.id} sx={{ '& + &': { mt: 1.5 } }}>
            <ListItem
              primary={section.label || section.name || section.id}
              onClick={() => section.collapsible && toggleSection(sectionKey)}
              rightAdornment={
                section.collapsible ? (
                  <Icon
                    color="inherit"
                    size="xsmall"
                    icon={collapsed ? faAngleDown : faAngleUp}
                    sx={{ color: DreamcoatNavColors.submenuItemTitle }}
                  />
                ) : undefined
              }
              sx={{
                px: 1,
                py: 1,
                color: 'primary.contrastText',
                '& .MuiListItemText-root .MuiTypography-root': {
                  color: DreamcoatNavColors.submenuItemTitle,
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: '20px',
                },
              }}
              clickable={!!section.collapsible}
            />
            <Collapse in={!collapsed}>
              {section.children.map(renderSubItem)}
            </Collapse>
          </Box>
        );
      })}
    </>
  );
};

const AdminNavItems = ({
  setActiveSubmenu,
  activeSubmenu,
}: {
  setActiveSubmenu: (id: DecoratedNavItem | null) => void;
  activeSubmenu: DecoratedNavItem | null;
}) => {
  const adminNavItems = useAdminNav();

  const renderNavItems = (items: DecoratedNavItem[], level = 0) => {
    return items.map((navItem, i) => {
      if (!navItem.enabled) return null;
      const opensSubmenu = hasNavItemChildren(navItem);
      return (
        <TopLevelNavItem
          key={navItem.id + i}
          title={navItem.name}
          icon={level === 0 ? navItem.icon : undefined}
          childrenItems={getNavItemChildren(navItem)}
          subnavOpen={activeSubmenu?.id === navItem.id}
          route={opensSubmenu ? undefined : navItem.route}
          reactRoute={opensSubmenu ? undefined : navItem.reactRoute}
          onClick={() => {
            if (opensSubmenu) {
              setActiveSubmenu(navItem);
              return;
            }
            setActiveSubmenu(null);
          }}
          target={navItem.target}
          rel={navItem.rel}
          level={level}
        ></TopLevelNavItem>
      );
    });
  };

  return (
    <List sx={{ m: 0, width: '100%', py: 0 }}>
      {renderNavItems(adminNavItems)}
    </List>
  );
};

const BottomNavItems = ({
  setActiveSubmenu,
  activeSubmenu,
  navItems,
  sx,
}: {
  setActiveSubmenu: (id: DecoratedNavItem | null) => void;
  activeSubmenu: DecoratedNavItem | null;
  navItems: DecoratedNavItem[];
  sx?: SxProps<Theme>;
}) => {
  const hasBottomItems = navItems.some(
    (navItem) => navItem.enabled && navItem.placement === 'bottom'
  );

  if (!hasBottomItems) {
    return null;
  }

  return (
    <Stack
      direction="column"
      alignItems="stretch"
      sx={[
        { boxShadow: '0px 0px 20px 0px #00000080', py: 1 },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <MainNavItems
        activeSubmenu={activeSubmenu}
        setActiveSubmenu={setActiveSubmenu}
        navItems={navItems}
        placement="bottom"
      />
    </Stack>
  );
};

const MainNavItems = ({
  setActiveSubmenu,
  activeSubmenu,
  navItems,
  placement,
}: {
  setActiveSubmenu: (id: DecoratedNavItem | null) => void;
  activeSubmenu: DecoratedNavItem | null;
  navItems: DecoratedNavItem[];
  placement: 'top' | 'bottom';
}) => {
  const renderNavItems = (items: DecoratedNavItem[], level = 0) => {
    return items
      .filter(
        (navItem) =>
          navItem.enabled &&
          (navItem.placement === 'bottom') === (placement === 'bottom')
      )
      .map((navItem, i) => {
        const opensSubmenu = hasNavItemChildren(navItem);
        return (
          <TopLevelNavItem
            key={navItem.id + i}
            title={navItem.name}
            icon={level === 0 ? navItem.icon : undefined}
            childrenItems={getNavItemChildren(navItem)}
            subnavOpen={activeSubmenu?.id === navItem.id}
            route={opensSubmenu ? undefined : navItem.route}
            reactRoute={opensSubmenu ? undefined : navItem.reactRoute}
            onClick={() => {
              if (opensSubmenu) {
                setActiveSubmenu(navItem);
                return;
              }
              setActiveSubmenu(null);
            }}
            target={navItem.target}
            rel={navItem.rel}
            level={level}
          ></TopLevelNavItem>
        );
      });
  };

  return (
    <List sx={{ m: 0, width: '100%', py: 0 }}>{renderNavItems(navItems)}</List>
  );
};
